import {useEffect} from 'react';

/**
 * Returns focus to `triggerRef` when the panel transitions from open to closed.
 * Avoids stealing focus on first mount.
 */
export function useFocusReturn(open, triggerRef, wasOpenRef) {
    useEffect(() => {
        if (open) {
            wasOpenRef.current = true;
            return;
        }
        if (wasOpenRef.current && triggerRef && triggerRef.current) {
            triggerRef.current.focus();
        }
    }, [open]);
}

/**
 * Calls `onClose` when the Escape key is pressed (only while `open`).
 */
export function useEscapeKey(open, onClose) {
    useEffect(() => {
        if (!open) {
            return;
        }
        const handler = e => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, onClose]);
}

/**
 * Traps Tab key navigation inside `panelRef`, cycling between first and last
 * focusable elements. Active only while `open`.
 */
export function useFocusTrap(open, panelRef) {
    useEffect(() => {
        if (!open || !panelRef.current) {
            return;
        }
        const trap = e => {
            if (e.key !== 'Tab') {
                return;
            }
            const els = Array.from(
                panelRef.current.querySelectorAll('button,a,[tabindex]:not([tabindex="-1"])')
            ).filter(el => !el.disabled);
            if (!els.length) {
                return;
            }
            if (e.shiftKey && document.activeElement === els[0]) {
                e.preventDefault();
                els[els.length - 1].focus();
            } else if (!e.shiftKey && document.activeElement === els[els.length - 1]) {
                e.preventDefault();
                els[0].focus();
            }
        };
        window.addEventListener('keydown', trap);
        return () => window.removeEventListener('keydown', trap);
    }, [open]);
}

